// This file has been modified from the original Blueprint file to match this project's linting rules and style and remove some unneeded code and inline some types.

/*
 * Copyright 2015 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

type Intent = 'none' | 'primary' | 'success' | 'warning' | 'danger';

const NS = 'bp5';

// modifiers
export const ACTIVE = `${NS}-active`;
export const ALIGN_LEFT = `${NS}-align-left`;
export const ALIGN_RIGHT = `${NS}-align-right`;
export const COMPACT = `${NS}-compact`;
export const DARK = `${NS}-dark`;
export const DISABLED = `${NS}-disabled`;
export const FILL = `${NS}-fill`;
export const INTERACTIVE = `${NS}-interactive`;
export const LARGE = `${NS}-large`;
export const MINIMAL = `${NS}-minimal`;
export const OUTLINED = `${NS}-outlined`;
export const ROUND = `${NS}-round`;
export const SELECTED = `${NS}-selected`;
export const SMALL = `${NS}-small`;

export const INTENT_PRIMARY = intentClass('primary')!;
export const INTENT_SUCCESS = intentClass('success')!;
export const INTENT_WARNING = intentClass('warning')!;
export const INTENT_DANGER = intentClass('danger')!;

export const FOCUS_DISABLED = `${NS}-focus-disabled`;
export const FOCUS_STYLE_MANAGER_IGNORE = `${NS}-focus-style-manager-ignore`;

// components
export const BUTTON = `${NS}-button`;
export const BUTTON_TEXT = `${BUTTON}-text`;

export const HTML_SELECT = `${NS}-html-select`;

export const HTML_TABLE = `${NS}-html-table`;
export const HTML_TABLE_BORDERED = `${HTML_TABLE}-bordered`;
export const HTML_TABLE_STRIPED = `${HTML_TABLE}-striped`;

export const INPUT = `${NS}-input`;
export const INPUT_GROUP = `${INPUT}-group`;

export const TEXT_AREA = `${NS}-text-area`;

export const KEY = `${NS}-key`;
export const KEY_COMBO = `${KEY}-combo`;
export const MODIFIER_KEY = `${NS}-modifier-key`;

const HOTKEY = `${NS}-hotkey`;
export const HOTKEY_LABEL = `${HOTKEY}-label`;

const MENU = `${NS}-menu`;
export const MENU_ITEM = `${MENU}-item`;

export const NON_IDEAL_STATE = `${NS}-non-ideal-state`;
export const NON_IDEAL_STATE_VISUAL = `${NON_IDEAL_STATE}-visual`;
export const NON_IDEAL_STATE_TEXT = `${NON_IDEAL_STATE}-text`;

export const SKELETON = `${NS}-skeleton`;

export const TAG = `${NS}-tag`;
export const TAG_REMOVE = `${TAG}-remove`;

export const TOAST = `${NS}-toast`;
export const TOAST_MESSAGE = `${TOAST}-message`;

export const TREE = `${NS}-tree`;
export const TREE_NODE = `${NS}-tree-node`;
export const TREE_NODE_CARET = `${TREE_NODE}-caret`;
export const TREE_NODE_CARET_CLOSED = `${TREE_NODE_CARET}-closed`;
export const TREE_NODE_CARET_NONE = `${TREE_NODE_CARET}-none`;
export const TREE_NODE_CARET_OPEN = `${TREE_NODE_CARET}-open`;
export const TREE_NODE_CONTENT = `${TREE_NODE}-content`;
export const TREE_NODE_EXPANDED = `${TREE_NODE}-expanded`;
export const TREE_NODE_ICON = `${TREE_NODE}-icon`;
export const TREE_NODE_LABEL = `${TREE_NODE}-label`;
export const TREE_NODE_LIST = `${TREE_NODE}-list`;
export const TREE_NODE_SECONDARY_LABEL = `${TREE_NODE}-secondary-label`;
export const TREE_NODE_SELECTED = `${TREE_NODE}-selected`;
export const TREE_ROOT = `${NS}-tree-root`;

export const ICON = `${NS}-icon`;
export const ICON_STANDARD = `${ICON}-standard`;
export const ICON_LARGE = `${ICON}-large`;
export const ICON_MUTED = `${ICON}-muted`;

export function iconClass(iconName: string | undefined) {
  if (iconName == null) {
    return undefined;
  }
  return iconName.indexOf(`${NS}-icon-`) === 0 ? iconName : `${NS}-icon-${iconName}`;
}

export function intentClass(intent: Intent | undefined) {
  if (intent == null || intent === 'none') {
    return undefined;
  }
  return `${NS}-intent-${intent.toLowerCase()}`;
}
